import React, { useState, useEffect, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  InputGroup,
  InputGroupText,
  Badge,
} from "reactstrap"
import { Form } from "react-bootstrap"

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons"
import { Link } from "react-router-dom"
import { UncontrolledTooltip } from "reactstrap"
import toastr from "toastr"
import TableContainer from "../../components/Common/TableContainer"
import DeleteModal from "components/Common/DeleteModal"
import apiURl from "url"

const Clients = () => {
  // Meta title
  document.title = "Shop Clients"

  const navigate = useNavigate()
  // Authorization check
  const CheckLog = localStorage.getItem("role")
  if (CheckLog !== "Admin" && CheckLog !== "Sub-Admin") {
    navigate("/")
  }

  // State management
  const [showPassword, setShowPassword] = useState(false)
  const [clients, setClients] = useState([])
  const [accountModal, setAccountModal] = useState(false)
  const [deleteModal, setDeleteModal] = useState(false)
  const [deleteID, setDeleteID] = useState(null)
  const [editClient, setEditClient] = useState(null)
  const [formData, setFormData] = useState({
    fullname: "",
    businessName: "",
    phone: "",
    email: "",
    fullAddress: "",
    services: "",
    websiteLink: "",
    businessLocations: "",
    logo: null,
    password: "",
  })

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  // Handle form input changes
  const handleInputChange = e => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleLogoChange = e => {
    const file = e.target.files[0]
    if (file) {
      setFormData(prev => ({ ...prev, logo: file }))
    }
  }

  // Fetch all clients
  const fetchAllClients = async () => {
    try {
      const response = await fetch(`${apiURl}/api/clients/fetchClients`)
      const json = await response.json()
      if (response.status === 200) setClients(json.clients.reverse())
    } catch (err) {
      console.error("Fetch Error:", err)
    }
  }

  // Fetch client data by ID for editing
  const fetchClientDataByID = async id => {
    try {
      const response = await fetch(`${apiURl}/api/clients/fetchClientByID`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      })
      const json = await response.json()
      if (response.status === 200 && json.status === "success") {
        const clientData = json.ClientDataByID[0]
        if (clientData) {
          setFormData({
            fullname: clientData.full_name || "",
            businessName: clientData.business_name || "",
            phone: clientData.phone || "",
            email: clientData.email || "",
            fullAddress: clientData.address || "",
            services: clientData.services || "",
            websiteLink: clientData.website || "",
            businessLocations: clientData.locations || "",
            logo: clientData.logo || "",
            password: "",
          })
          setEditClient(clientData)
          setAccountModal(true)
        }
      }
    } catch (err) {
      console.error("Fetch Client Error:", err)
    }
  }

  // Handle save (create or update)
  const handleSaveClient = async e => {
    e.preventDefault()

    // Form validation
    if (!formData.fullname.trim()) return toastr.error("Full Name is required")
    if (!formData.businessName.trim())
      return toastr.error("Business Name is required")
    if (!formData.phone.trim()) return toastr.error("Phone is required")
    if (!formData.email.trim()) return toastr.error("Email is required")
    if (!formData.password.trim() && !editClient)
      return toastr.error("Password is required")

    const method = editClient ? "PUT" : "POST"
    const url = editClient
      ? `${apiURl}/api/clients/edit-client/${editClient.id}`
      : `${apiURl}/api/clients/add-client`

    const formPayload = new FormData()
    formPayload.append("fullname", formData.fullname)
    formPayload.append("businessName", formData.businessName)
    formPayload.append("phone", formData.phone)
    formPayload.append("email", formData.email)
    formPayload.append("fullAddress", formData.fullAddress)
    formPayload.append("services", formData.services)
    formPayload.append("websiteLink", formData.websiteLink)
    formPayload.append("businessLocations", formData.businessLocations)
    formPayload.append("password", formData.password || "")

    if (formData.logo) {
      formPayload.append("logo", formData.logo)
    }

    try {
      const response = await fetch(url, {
        method,
        body: formPayload,
      })

      const json = await response.json()

      if (response.ok) {
        toastr.success(editClient ? "Client updated!" : "Client added!")
        fetchAllClients()
        setAccountModal(false)
        setEditClient(null)
        setFormData({
          fullname: "",
          businessName: "",
          phone: "",
          email: "",
          fullAddress: "",
          services: "",
          websiteLink: "",
          businessLocations: "",
          logo: null,
          password: "",
        })
      } else {
        toastr.error(json.message || "Failed to save client")
      }
    } catch (err) {
      console.error("Save Error:", err)
      toastr.error("Failed to save client")
    }
  }

  // Handle delete
  const deleteClient = async () => {
    try {
      const response = await fetch(
        `${apiURl}/api/clients/delete-client/${deleteID}`,
        {
          method: "DELETE",
        }
      )
      if (response.ok) {
        toastr.success("Client deleted successfully")
        setDeleteModal(false)
        fetchAllClients()
      } else {
        toastr.error("Failed to delete client")
      }
    } catch (err) {
      console.error("Delete Error:", err)
      toastr.error("Failed to delete client")
    }
  }

  // Initialize component
  useEffect(() => {
    fetchAllClients()
  }, [])

  // Table columns
  const columns = useMemo(
    () => [
      {
        Header: "Full Name",
        accessor: "full_name",
        Cell: cellProps => <span>{cellProps.value}</span>,
      },
      {
        Header: "Business Name",
        accessor: "business_name",
        Cell: cellProps => <span>{cellProps.value}</span>,
      },
      {
        Header: "Email",
        accessor: "email",
        Cell: cellProps => <span>{cellProps.value}</span>,
      },
      {
        Header: "Phone",
        accessor: "phone",
        Cell: cellProps => <span>{cellProps.value}</span>,
      },
      {
        Header: "Status",
        accessor: "status_",
        Cell: ({ row }) => {
          const isActive = row.original.status_ === "Active"
          const handleToggle = async () => {
            const newStatus = isActive ? "Not Active" : "Active"
            try {
              const res = await fetch(
                `${apiURl}/api/clients/update-status/${row.original.id}`,
                {
                  method: "PUT",
                  headers: {
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify({ status_: newStatus }),
                }
              )
              if (res.ok) {
                toastr.success("Status updated successfully")
                fetchAllClients()
              } else {
                toastr.error("Failed to update status")
              }
            } catch (err) {
              toastr.error("Server error")
              console.error(err)
            }
          }
          return (
            <Form>
              <Form.Check
                type="switch"
                id={`status-switch-${row.original.id}`}
                label={
                  isActive ? (
                    <Badge pill className="bg-success">
                      Active
                    </Badge>
                  ) : (
                    <Badge pill className="bg-danger">
                      Inactive
                    </Badge>
                  )
                }
                defaultChecked={isActive}
                onChange={handleToggle}
                style={{
                  color: isActive ? "green" : "red",
                  fontWeight: "bold",
                }}
              />
            </Form>
          )
        },
      },
      {
        Header: "Action",
        accessor: "id",
        Cell: cellProps => (
          <div className="d-flex gap-3">
            <Link
              to="#"
              onClick={() => fetchClientDataByID(cellProps.value)}
              className="text-primary"
            >
              <i
                className="mdi mdi-eye font-size-18"
                id={`viewtooltip-${cellProps.value}`}
              />
              <UncontrolledTooltip
                placement="top"
                target={`viewtooltip-${cellProps.value}`}
              >
                View
              </UncontrolledTooltip>
            </Link>
            <Link
              to="#"
              className="text-danger"
              onClick={() => {
                setDeleteID(cellProps.value)
                setDeleteModal(true)
              }}
            >
              <i
                className="mdi mdi-delete font-size-18"
                id={`deletetooltip-${cellProps.value}`}
              />
              <UncontrolledTooltip
                placement="top"
                target={`deletetooltip-${cellProps.value}`}
              >
                Delete
              </UncontrolledTooltip>
            </Link>
          </div>
        ),
      },
    ],
    []
  )

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={deleteClient}
        onCloseClick={() => setDeleteModal(false)}
      />

      <div className="page-content">
        <Container fluid>
          <Row>
            <Col xl="6">
              <h5 className="mt-2">Clients Management</h5>
            </Col>
            <Col>
              <div className="float-end">
                <Button
                  color="primary"
                  className="btn-sm w-md"
                  onClick={() => {
                    setFormData({
                      fullname: "",
                      businessName: "",
                      phone: "",
                      email: "",
                      fullAddress: "",
                      services: "",
                      websiteLink: "",
                      businessLocations: "",
                      logo: null,
                      password: "",
                    })
                    setEditClient(null)
                    setAccountModal(true)
                  }}
                >
                  <i className="fa fa-fw fa-plus me-1" />
                  Create Client
                </Button>
              </div>
            </Col>
          </Row>

          <Row className="pt-3">
            <Col lg="12">
              <Card>
                <CardBody>
                  <TableContainer
                    columns={columns}
                    data={clients}
                    isGlobalFilter={true}
                    isAddOptions={false}
                    className="custom-table"
                    customPageSize={10}
                    FilterColumns={false}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Client Modal */}
      <Modal
        isOpen={accountModal}
        toggle={() => setAccountModal(!accountModal)}
        size="lg"
        centered
      >
        <Form onSubmit={handleSaveClient}>
          <ModalHeader toggle={() => setAccountModal(!accountModal)}>
            {editClient ? "Edit Client" : "Create New Client"}
          </ModalHeader>
          <ModalBody>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Full Name *</Label>
                  <Input
                    type="text"
                    name="fullname"
                    value={formData.fullname}
                    onChange={handleInputChange}
                    placeholder="Enter full name"
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Business Name *</Label>
                  <Input
                    type="text"
                    name="businessName"
                    value={formData.businessName}
                    onChange={handleInputChange}
                    placeholder="Enter business name"
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Phone *</Label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Enter phone number"
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter email"
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Password *</Label>
                  <InputGroup>
                    <Input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Enter password"
                      required={!editClient}
                    />
                    <InputGroupText
                      onClick={togglePasswordVisibility}
                      style={{ cursor: "pointer" }}
                    >
                      <FontAwesomeIcon
                        icon={showPassword ? faEyeSlash : faEye}
                      />
                    </InputGroupText>
                  </InputGroup>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Full Address</Label>
                  <Input
                    type="text"
                    name="fullAddress"
                    value={formData.fullAddress}
                    onChange={handleInputChange}
                    placeholder="Enter full address"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Services</Label>
                  <Input
                    type="text"
                    name="services"
                    value={formData.services}
                    onChange={handleInputChange}
                    placeholder="Enter services"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Website Link</Label>
                  <Input
                    type="url"
                    name="websiteLink"
                    value={formData.websiteLink}
                    onChange={handleInputChange}
                    placeholder="Enter website link"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Business Locations</Label>
                  <Input
                    type="text"
                    name="businessLocations"
                    value={formData.businessLocations}
                    onChange={handleInputChange}
                    placeholder="Enter business locations"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Logo</Label>
                  <Input
                    type="file"
                    name="logo"
                    accept="image/*"
                    onChange={handleLogoChange}
                  />
                </FormGroup>
                {formData.logo && (
                  <div className="mb-3">
                    <img
                      src={
                        typeof formData.logo === "string"
                          ? `${apiURl}${formData.logo}`
                          : URL.createObjectURL(formData.logo)
                      }
                      alt="Preview"
                      style={{
                        width: "70px",
                        height: "70px",
                        objectFit: "cover",
                        borderRadius: "5px",
                      }}
                    />
                  </div>
                )}
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              onClick={() => {
                setFormData({
                  fullname: "",
                  businessName: "",
                  phone: "",
                  email: "",
                  fullAddress: "",
                  services: "",
                  websiteLink: "",
                  businessLocations: "",
                  logo: null,
                  password: "",
                })
                setEditClient(null)
                setAccountModal(false)
              }}
            >
              Cancel
            </Button>
            <Button color="primary" type="submit">
              {editClient ? "Update Client" : "Create Client"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </React.Fragment>
  )
}

export default Clients
